/**
 * The offline-first sync engine.
 *
 * Reads always come from IndexedDB via the store; writes go to the store, to
 * IndexedDB and into an outbox, and are pushed when the network allows. The
 * server merges per field with last-writer-wins, so a phone that was offline
 * for a day merges cleanly instead of clobbering the team's work.
 */
import {
  Clock, COLLECTIONS, ENTITY_NAMES,
  type ChangeSet, type EntityName, type Mutation, type PullResponse, type PushResponse,
} from '@kolibri/shared';
import { api, ApiError } from './api';
import { now } from './clock';
import { serverUrl, sessionToken } from './server';
import * as idb from './idb';
import { currentLocale, translate } from '../i18n/i18n';
import { applyChanges, byId, forgetLocal, hydrate, notifyStore, purgedRows, reset } from './store';

export type SyncState = 'starting' | 'synced' | 'syncing' | 'offline' | 'error';

export interface SyncStatus {
  state: SyncState;
  pending: number;
  lastSyncedAt: number | null;
  message?: string;
}

const listeners = new Set<(status: SyncStatus) => void>();
let status: SyncStatus = { state: 'starting', pending: 0, lastSyncedAt: null };

export function subscribeSync(listener: (status: SyncStatus) => void): () => void {
  listeners.add(listener);
  listener(status);
  return () => listeners.delete(listener);
}

function setStatus(patch: Partial<SyncStatus>): void {
  status = { ...status, ...patch };
  for (const listener of listeners) listener(status);
}

export const getStatus = (): SyncStatus => status;

/**
 * Whether this row is still waiting for the network.
 *
 * `status.pending` counts the queue, which answers "is anything unsent" and
 * not "is *this* unsent" — and the second is the one a conversation needs, so
 * that a message written in a tunnel can say it has not left yet instead of
 * looking exactly like one that arrived. Scanning the outbox is cheap because
 * a full outbox is a short list; if it ever is not, nothing is being sent and
 * that is the larger problem.
 *
 * Subscribe with `subscribeSync`: the queue only changes where the status
 * does, so a listener there already sees every change to this.
 */
export const isPendingRow = (entity: EntityName, entityId: string): boolean =>
  outbox.some((mutation) => mutation.entity === entity && mutation.entityId === entityId);

/* ------------------------------------------------------------------ engine */

const CLIENT_KEY = 'kolibri.client-id';

let clientId = localStorage.getItem(CLIENT_KEY) ?? '';
if (!clientId) {
  clientId = crypto.randomUUID().slice(0, 8);
  localStorage.setItem(CLIENT_KEY, clientId);
}

/**
 * The stamp that decides which of two concurrent edits wins.
 *
 * It is handed `now` — this device's reading of the *server's* clock — rather
 * than left to read its own. A browser five minutes fast would otherwise stamp
 * every write five minutes into the future, and last-writer-wins would mean
 * that browser always wins for as long as its clock stays wrong. `observe`
 * catches a clock that is behind; nothing catches one that is ahead.
 */
export const clock = new Clock(clientId, now);

/**
 * This device's identity, as the text CRDT uses it.
 *
 * The same one the hybrid logical clock uses, which is right: a CRDT agent id
 * has to belong to exactly one writer, and "this browser profile" is the unit
 * this app already has for that.
 */
export const agentId = (): string => clientId;

let workspaceId = '';
let cursor = 0;
let outbox: Mutation[] = [];
let stream: EventSource | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let pulling: Promise<void> | null = null;
let retryDelay = 1000;

export function currentWorkspace(): string {
  return workspaceId;
}

/** Load the cached workspace from disk, then catch up with the server. */
export async function start(id: string): Promise<void> {
  if (workspaceId === id) return;
  stop();
  workspaceId = id;
  setStatus({ state: 'starting' });

  const cached = await idb.getMeta<{ workspaceId: string; cursor: number }>('sync');
  if (cached?.workspaceId === id) {
    cursor = cached.cursor;
    await Promise.all(ENTITY_NAMES.map(async (entity) => hydrate(entity, await idb.readAll(entity))));
    notifyStore();
  } else {
    cursor = 0;
    reset();
    await idb.clearAll();
  }

  outbox = await idb.readAll<Mutation>(idb.OUTBOX);
  setStatus({ pending: outbox.length });

  await flush();
  await pull();
  connect();
  void idb.requestPersistence();
}

export function stop(): void {
  stream?.close();
  stream = null;
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = null;
}

export async function signOutLocal(): Promise<void> {
  stop();
  workspaceId = '';
  cursor = 0;
  outbox = [];
  reset();
  await idb.clearAll();
}

/* -------------------------------------------------------------------- pull */

export function pull(): Promise<void> {
  if (pulling) return pulling;
  pulling = (async () => {
    if (!workspaceId) return;
    setStatus({ state: 'syncing' });
    try {
      let more = true;
      while (more) {
        const response = await api.get<PullResponse>(`/api/sync/pull?workspace=${workspaceId}&since=${cursor}`);
        if (response.reset && cursor === 0) {
          reset();
          await idb.clearAll();
        }
        applyChanges(response.changes, isPendingRow);
        await persist(response.changes);
        const before = cursor;
        cursor = response.cursor;
        await idb.setMeta('sync', { workspaceId, cursor });
        // The server says whether it truncated. The `cursor > before` guard
        // stays: a server that ever answered without advancing would otherwise
        // spin here forever.
        more = !!response.hasMore && response.cursor > before;
      }
      retryDelay = 1000;
      setStatus({ state: outbox.length ? 'syncing' : 'synced', lastSyncedAt: Date.now(), message: undefined });
    } catch (err) {
      handleError(err);
    } finally {
      pulling = null;
    }
  })();
  return pulling;
}

async function persist(changes: ChangeSet): Promise<void> {
  const entries = Object.entries(changes)
    .filter(([, rows]) => rows?.length)
    .map(([entity, rows]) => ({ store: entity, values: rows as unknown[] }));
  if (entries.length) await idb.putMany(entries);
  // A purge names a row that is gone for good, so it leaves the device too —
  // otherwise a reload would read the tombstone straight back out of IndexedDB
  // and put it in the trash again.
  for (const { store, keys } of purgedRows(changes)) await idb.removeMany(store, keys);
}

/* -------------------------------------------------------------------- push */

/** Queue a local change: store first, network whenever it comes back. */
export function enqueue(entity: EntityName, entityId: string, patch: Record<string, unknown>, op: 'upsert' | 'delete' = 'upsert'): void {
  const mutation: Mutation = {
    id: crypto.randomUUID(),
    entity,
    entityId,
    op,
    patch,
    hlc: clock.now(),
  };
  outbox.push(mutation);
  setStatus({ pending: outbox.length, state: 'syncing' });
  void idb.put(idb.OUTBOX, mutation);
  scheduleFlush();
}

function scheduleFlush(delay = 250): void {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => void flush(), delay);
}

export async function flush(): Promise<void> {
  if (!workspaceId || !outbox.length) return;
  if (!navigator.onLine) {
    setStatus({ state: 'offline' });
    return;
  }
  const batch = outbox.slice(0, 200);
  try {
    const response = await api.post<PushResponse>('/api/sync/push', { workspaceId, clientId, mutations: batch });
    const accepted = new Set([...response.accepted, ...response.rejected.map((r) => r.id)]);
    outbox = outbox.filter((m) => !accepted.has(m.id));
    await idb.removeMany(idb.OUTBOX, [...accepted]);

    if (response.rejected.length) {
      console.warn('[sync] server rejected mutations', response.rejected);
      setStatus({ message: response.rejected[0].reason });
      // The change was already applied locally, and a row the server did not
      // change will not come back in a delta pull — so the rows a rejection
      // touched are re-read by hand. Without this the screen keeps showing an
      // edit the server refused.
      await undo(batch.filter((mutation) => response.rejected.some((r) => r.id === mutation.id)));
    }
    // The server may have decided values for us (task identifiers, defaults).
    applyChanges(response.patched, isPendingRow);
    await persist(response.patched);

    setStatus({ pending: outbox.length, state: outbox.length ? 'syncing' : 'synced', lastSyncedAt: Date.now() });
    retryDelay = 1000;
    if (outbox.length) scheduleFlush(0);
    else await pull();
  } catch (err) {
    handleError(err);
  }
}

/**
 * Read one row back from the server and take it as it is.
 *
 * A pull is a delta, so a row the server did not change is a row no pull will
 * ever mention again — and that is the hole this fills. Two callers need it
 * and they need the same thing: `undo` below, for a write the server refused,
 * and `resync` for a row this device has reason to believe it holds wrongly.
 *
 * Deliberately *not* merged into what is here. The point of asking is that the
 * local copy is not trusted, and the outbox is empty of it by the time either
 * caller asks — `flush` drops a mutation before it undoes it, and `resync`
 * checks.
 */
async function reread(entity: EntityName, id: string): Promise<void> {
  const row = await api.get<Record<string, unknown>>(`/api/${COLLECTIONS[entity]}/${id}`);
  applyChanges({ [entity]: [row] } as ChangeSet);
  await persist({ [entity]: [row] } as ChangeSet);
}

/**
 * Throw away what this device holds for one row and take the server's.
 *
 * For the caller that can *tell* its copy is wrong — a page whose CRDT and
 * whose text disagree, say, which the server never stores and which no merge
 * can repair, because merging a good state into a bad one keeps the bad one.
 * Nothing happens while the row is still queued: then the disagreement is just
 * this device being ahead, which is the ordinary case and not a fault.
 *
 * Quiet on failure. It is a repair, not a write somebody is waiting on, and
 * the screen it was called from is no worse off than before it tried.
 */
export async function resync(entity: EntityName, id: string): Promise<void> {
  if (isPendingRow(entity, id)) return;
  try {
    await reread(entity, id);
  } catch {
    /* offline, or gone — the next attempt is the next time somebody looks */
  }
}

/**
 * Put back what the server would not take.
 *
 * One read per rejected row, which is fine: a rejection is rare by design — the
 * interface does not offer moves the rules forbid, so this is the path for the
 * API, another device, or a rule that changed while somebody was offline.
 */
async function undo(rejected: Mutation[]): Promise<void> {
  const seen = new Set<string>();
  for (const mutation of rejected) {
    const key = `${mutation.entity}:${mutation.entityId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    try {
      await reread(mutation.entity, mutation.entityId);
    } catch (error) {
      /*
       * A row the server does not have, because it refused to create it.
       *
       * "The next full sync settles it" was the comment here, and it does not:
       * a pull is a delta, and a row that was never created is a row no delta
       * will ever mention. So the optimistic copy stayed on the device
       * indefinitely — visible, editable, and gone the moment anybody reloaded
       * from a snapshot. It looked exactly like a duplicate the server had
       * accepted.
       *
       * Rare by design, as the note above says, because the interface does not
       * offer moves the rules forbid — but connecting a mailbox is the case
       * where a person can trip a uniqueness rule by typing, and it turned two
       * rows out of one.
       *
       * Narrow on purpose. Only a definite 404, and only for a row this device
       * invented: a row that has a `seq` came down a pull, so the server had it
       * once and a 404 now means something else — deleted elsewhere, or a
       * permission that changed — and dropping it locally would be guessing.
       */
      const invented = !byId(mutation.entity, mutation.entityId)?.seq;
      if (error instanceof ApiError && error.status === 404 && invented) {
        forgetLocal(mutation.entity, mutation.entityId);
        await idb.remove(mutation.entity, mutation.entityId);
        continue;
      }
      // Anything else: gone, unreadable, or a bad moment on the network.
    }
  }
}

function handleError(err: unknown): void {
  const offline = !navigator.onLine || (err instanceof ApiError && err.status === 0);
  if (offline) {
    setStatus({ state: 'offline', message: undefined });
  } else if (err instanceof ApiError && err.status === 401) {
    setStatus({ state: 'error', message: translate(currentLocale(), 'sync.sessionExpired') });
    window.dispatchEvent(new CustomEvent('kolibri:signed-out'));
    return;
  } else {
    setStatus({ state: 'error', message: err instanceof Error ? err.message : translate(currentLocale(), 'sync.failed') });
  }
  retryDelay = Math.min(retryDelay * 2, 30_000);
  setTimeout(() => {
    if (navigator.onLine) void flush().then(() => pull());
  }, retryDelay);
}

/* --------------------------------------------------------------- realtime */

/**
 * A frame on the sync stream that is not a change.
 *
 * The stream carries `change`, which is this file's own business, and anything
 * else a capability wants pushed to it. Presence is the one today, and it used
 * to be four calls from here into `modules/chat/presence` — the sync engine
 * knowing which capability wanted its frames, which is backwards: sync owns the
 * connection, the capability owns what the frame means.
 */
export interface StreamHandler {
  /** The event name the server sends. */
  event: string;
  /** `first` is true for the first frame after the connection opened. */
  onFrame(data: string, first: boolean): void;
  /** The connection opened, or came back after a drop. */
  onOpen?(): void;
  /** The connection went away; whatever this was showing is now stale. */
  onDrop?(): void;
}

const handlers: StreamHandler[] = [];

/** @port a handler for one kind of frame on the live connection */
export function onStream(handler: StreamHandler): void {
  if (!handlers.includes(handler)) handlers.push(handler);
}

function connect(): void {
  stream?.close();
  if (!workspaceId) return;
  // `EventSource` cannot set a header, which is why the server has always read
  // `access_token` from the query for this one endpoint. A browser sends
  // neither and is authenticated by the cookie `withCredentials` carries.
  const token = sessionToken();
  const auth = token ? `&access_token=${encodeURIComponent(token)}` : '';
  stream = new EventSource(
    serverUrl(`/api/sync/stream?workspace=${workspaceId}&client=${clientId}${auth}`),
    { withCredentials: true },
  );
  stream.addEventListener('change', () => void pull());
  // Anything else riding the same connection is handled entirely separately: it
  // carries no cursor, so it never triggers a pull and a dropped frame costs
  // nothing but a stale dot until the next one.
  let first = true;
  for (const handler of handlers) {
    stream.addEventListener(handler.event, (event) => {
      try {
        handler.onFrame((event as MessageEvent).data, first);
      } catch {
        /* malformed frame: the next one will be fine */
      }
    });
  }
  stream.addEventListener('error', () => {
    // EventSource reconnects on its own; a pull on recovery closes any gap.
    for (const handler of handlers) handler.onDrop?.();
    if (stream?.readyState === EventSource.CLOSED) setTimeout(connect, 5000);
  });
  stream.addEventListener('open', () => {
    first = true;
    for (const handler of handlers) handler.onOpen?.();
    void pull();
  });
}

window.addEventListener('online', () => {
  setStatus({ state: 'syncing' });
  void flush().then(() => pull());
  connect();
});
window.addEventListener('offline', () => setStatus({ state: 'offline' }));
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && navigator.onLine) void pull();
});
setInterval(() => {
  if (navigator.onLine && workspaceId && document.visibilityState === 'visible') void pull();
}, 60_000);

